import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { Text } from "../../components/ui/Text";
import { useStore } from "../../context/StoreContext";
import { api } from "services/api";
import { getCountryByCode } from "../../utils/countryCurrencyData";

interface AddressField {
  key: string;
  label: string;
}

const AVAILABLE_FIELDS: AddressField[] = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "company", label: "Company" },
  { key: "address1", label: "Address Line 1" },
  { key: "address2", label: "Address Line 2" },
  { key: "city", label: "City" },
  { key: "province", label: "Province/State" },
  { key: "postalCode", label: "Postal Code" },
  { key: "country", label: "Country" },
  { key: "phone", label: "Phone" },
];

export const AddressFieldSettings: React.FC = () => {
  const { t } = useTranslation();
  const { store, refreshStore, loading: storeLoading } = useStore();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const loading = storeLoading;

  // Store locations (countries) available for address field configuration
  const [storeLocations, setStoreLocations] = useState<Array<{ code: string; name: string }>>([]);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Get configured countries from store (filtered to only show countries in store locations)
  // Normalize country codes for comparison (backend stores in uppercase)
  const configuredCountries = store?.settings?.mandatoryAddressFieldsByCountry
    ? Object.keys(store.settings.mandatoryAddressFieldsByCountry).filter((countryCode) =>
        storeLocations.some((loc) => loc.code.toUpperCase() === countryCode.toUpperCase())
      )
    : [];

  // Fetch store locations
  useEffect(() => {
    const fetchStoreLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await api.get("/locations/admin?isActive=true");
        const locations = response.data.locations || [];
        // Map locations to country codes and names
        const locationOptions = locations.map((loc: any) => {
          const country = getCountryByCode(loc.countryCode);
          return {
            code: loc.countryCode,
            name: country?.name || loc.country || loc.countryCode,
          };
        });
        setStoreLocations(locationOptions);
      } catch (err: any) {
        setError(t("admin.addressFieldSettings.errors.loadLocations") || "Failed to load store locations");
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchStoreLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize form fields when store data is loaded or country changes
  useEffect(() => {
    if (store && !storeLoading) {
      if (selectedCountryCode) {
        // Normalize country code to uppercase (backend stores in uppercase)
        const normalizedCountryCode = selectedCountryCode.toUpperCase();
        // Load country-specific mandatory fields
        const countryFields = store.settings?.mandatoryAddressFieldsByCountry?.[normalizedCountryCode];
        if (countryFields && countryFields.length > 0) {
          setSelectedFields(countryFields);
        } else {
          // Use defaults for new country (fall back to global settings or common defaults)
          const globalFields = store.settings?.mandatoryAddressFields || [
            "firstName",
            "lastName",
            "address1",
            "city",
            "postalCode",
            "country",
          ];
          setSelectedFields(globalFields);
        }
      } else {
        // Use legacy store-level mandatory fields as defaults
        const globalFields = store.settings?.mandatoryAddressFields || [
          "firstName",
          "lastName",
          "address1",
          "city",
          "postalCode",
          "country",
        ];
        setSelectedFields(globalFields);
      }
    }
  }, [store, storeLoading, selectedCountryCode]);

  const handleFieldToggle = (fieldKey: string) => {
    setSelectedFields((prev) => {
      if (prev.includes(fieldKey)) {
        return prev.filter((key) => key !== fieldKey);
      } else {
        return [...prev, fieldKey];
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!selectedCountryCode) {
      setError(t("admin.addressFieldSettings.pleaseSelectCountry") || "Please select a country");
      return;
    }

    setSaving(true);
    try {
      // Normalize country code to uppercase (backend stores in uppercase)
      const normalizedCountryCode = selectedCountryCode.toUpperCase();
      await api.put("/store/address-field-settings", {
        countryCode: normalizedCountryCode,
        mandatoryAddressFields: selectedFields,
      });
      setSuccess(t("admin.addressFieldSettings.success") || "Address field settings updated successfully");
      await refreshStore();
      // Update selected country code to match the normalized version
      setSelectedCountryCode(normalizedCountryCode);
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.addressFieldSettings.errors.update") || "Failed to update address field settings");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCountry = async (countryCode: string) => {
    const countryName = getCountryByCode(countryCode)?.name || countryCode;
    if (!window.confirm(t("admin.addressFieldSettings.removeCountryConfirm", { country: countryName }) || `Remove address field configuration for ${countryName}?`)) {
      return;
    }

    try {
      setError(null);
      // Normalize country code to uppercase (backend stores in uppercase)
      const normalizedCountryCode = countryCode.toUpperCase();
      // Get current mandatory fields for this country
      const currentFields = store?.settings?.mandatoryAddressFieldsByCountry?.[normalizedCountryCode];
      if (currentFields) {
        // Set to empty array to effectively remove the country config
        await api.put("/store/address-field-settings", {
          countryCode: normalizedCountryCode,
          mandatoryAddressFields: [],
        });
        await refreshStore();
        // Check if currently selected country matches (case-insensitive)
        if (selectedCountryCode.toUpperCase() === normalizedCountryCode) {
          setSelectedCountryCode("");
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.addressFieldSettings.errors.removeCountry") || "Failed to remove country configuration");
    }
  };

  if (loading) {
    return (
      <Container>
        <Stack spacing="lg">
          <div className="flex justify-center items-center min-h-[400px]">
            <Spinner size="lg" />
          </div>
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <Heading size="lg">{t("admin.addressFieldSettings.title") || "Address Field Settings"}</Heading>
        <Text className="text-gray-600">
          {t("admin.addressFieldSettings.description") ||
            "Configure which address fields are mandatory for customers when creating an address, per country. Fields not selected will be optional."}
        </Text>

        {error && (
          <Alert variant="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loadingLocations && (
          <Alert variant="info">
            <Text size="sm">{t("admin.addressFieldSettings.loadingLocations") || "Loading store locations..."}</Text>
          </Alert>
        )}

        {!loadingLocations && storeLocations.length === 0 && (
          <Alert variant="warning">
            <Text size="sm">
              {t("admin.addressFieldSettings.noLocationsFound") ||
                "No locations found. Please add at least one location (country) in the Locations section before configuring address fields."}
            </Text>
          </Alert>
        )}

        {!loadingLocations && storeLocations.length > 0 && (
          <>
            {/* Country Selection */}
            <Card padding="md">
              <Stack spacing="md">
                <Heading size="md">{t("admin.addressFieldSettings.selectCountry") || "Select Country"}</Heading>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("admin.addressFieldSettings.country") || "Country"}
                  </label>
                  <select
                    value={selectedCountryCode}
                    onChange={(e) => setSelectedCountryCode(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t("admin.addressFieldSettings.selectCountryPlaceholder") || "Select a country"}</option>
                    {storeLocations.map((loc) => (
                      <option key={loc.code} value={loc.code}>
                        {loc.name} ({loc.code})
                      </option>
                    ))}
                  </select>
                </div>
              </Stack>
            </Card>

            {/* Configured Countries List */}
            {configuredCountries.length > 0 && (
              <Card padding="md">
                <Stack spacing="md">
                  <Heading size="md">{t("admin.addressFieldSettings.configuredCountries") || "Configured Countries"}</Heading>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {configuredCountries.map((countryCode) => {
                      const countryName = getCountryByCode(countryCode)?.name || countryCode;
                      const normalizedCountryCode = countryCode.toUpperCase();
                      const fields = store?.settings?.mandatoryAddressFieldsByCountry?.[normalizedCountryCode] || [];
                      return (
                        <div key={countryCode} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">{countryName}</div>
                            <div className="text-sm text-gray-500">
                              {fields.length > 0
                                ? `${fields.length} mandatory field${fields.length !== 1 ? "s" : ""}`
                                : "No mandatory fields"}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Find matching location code (case-insensitive)
                                const matchingLocation = storeLocations.find(
                                  (loc) => loc.code.toUpperCase() === normalizedCountryCode
                                );
                                setSelectedCountryCode(matchingLocation?.code || countryCode);
                              }}
                            >
                              {t("common.edit") || "Edit"}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteCountry(normalizedCountryCode)}
                            >
                              {t("common.remove") || "Remove"}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Stack>
              </Card>
            )}

            {/* Field Selection Form */}
            {selectedCountryCode && (
              <form onSubmit={handleSubmit}>
                <Card padding="lg">
                  <Stack spacing="md">
                    <Heading size="md">
                      {t("admin.addressFieldSettings.selectFields") || "Select Mandatory Fields"}
                      {selectedCountryCode && (
                        <span className="ml-2 text-base font-normal text-gray-600">
                          ({getCountryByCode(selectedCountryCode)?.name || selectedCountryCode})
                        </span>
                      )}
                    </Heading>
                    <Text className="text-sm text-gray-600">
                      {t("admin.addressFieldSettings.selectFieldsDescription") ||
                        "Check the fields that should be required when customers create an address for this country."}
                    </Text>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      {AVAILABLE_FIELDS.map((field) => (
                        <label key={field.key} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedFields.includes(field.key)}
                            onChange={() => handleFieldToggle(field.key)}
                            className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm font-medium text-gray-700">{field.label}</span>
                        </label>
                      ))}
                    </div>

                    {selectedFields.length === 0 && (
                      <Alert variant="warning">
                        {t("admin.addressFieldSettings.warningNoFields") ||
                          "Warning: No mandatory fields selected. At least one field should be mandatory for proper address validation."}
                      </Alert>
                    )}

                    <div className="flex gap-3 justify-end mt-6">
                      <Button type="submit" disabled={saving}>
                        {saving ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            {t("common.saving") || "Saving..."}
                          </>
                        ) : (
                          t("common.save") || "Save Settings"
                        )}
                      </Button>
                    </div>
                  </Stack>
                </Card>
              </form>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
};
